import { Button, Spacer, Stack } from "@chakra-ui/react";
import { Map } from "immutable";
import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AdminFormButtons } from "src/components/Layout/AdminFormButtons";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useOrganization } from "src/hooks/useOrganization";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { trackFormAction } from "../components/trackFormAction";
import { useEnqueueBulkFormVerify } from "../components/useEnqueueBulkFormVerify";
import type { BulkTargets } from "../types";
import { CompositeStatus, Mixed, VerificationInput } from "./VerificationInput";
import {
  processVerificationSummary,
  type BulkVerificationSummary,
} from "./bulkVerificationSummary";

type BulkVerificationFormAsyncProps = {
  bulkTargets: BulkTargets;
  selectionSize: number;
  formIds: uuid[];
  applicableVerificationsSummaries: GQL.GetApplicableVerificationsSummaries;
};

export const BulkVerificationFormAsync: React.FC<
  BulkVerificationFormAsyncProps
> = ({
  bulkTargets,
  selectionSize,
  formIds,
  applicableVerificationsSummaries,
}) => {
  const navigate = useNavigate();
  const toast = useAvelaToast();
  const organization = useOrganization();
  const [searchParams] = useSearchParams();
  const enqueueBulkFormVerify = useEnqueueBulkFormVerify(
    bulkTargets,
    selectionSize
  );
  const isExclusionMode = "cacheKey" in bulkTargets;

  const verificationSummaries = useMemo<readonly BulkVerificationSummary[]>(
    () =>
      applicableVerificationsSummaries.form_verification.map((verification) =>
        processVerificationSummary(verification, formIds)
      ),
    [applicableVerificationsSummaries, formIds]
  );

  const [newStatuses, setNewStatuses] = useState(Map<uuid, CompositeStatus>());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const updates = verificationSummaries.flatMap((summary) => {
        const newStatus = newStatuses.get(summary.verification.id);
        if (
          !newStatus ||
          newStatus === summary.compositeStatus ||
          newStatus === Mixed
        ) {
          return [];
        }

        if (!isExclusionMode) {
          const applicableIds = summary.applicableAppIds.filter(
            (appId) =>
              summary.applicableVerificationResults.get(
                appId,
                GQL.verification_status_enum.Pending
              ) !== newStatus
          );
          if (applicableIds.isEmpty()) {
            return [];
          }
        }

        return [
          {
            form_verification_id: summary.verification.id,
            verification_status: newStatus,
          },
        ];
      });

      if (!updates.length) {
        toast({ title: "No changes made." });
        navigate(-1);
        return;
      }

      trackFormAction({
        verb: "Confirmed",
        actionType: "VERIFY_FORM",
        bulk: true,
        count: selectionSize,
        async: true,
      });

      const queued = await enqueueBulkFormVerify(updates);
      if (!queued) {
        return;
      }

      const formsListPath = organization
        .map((org) =>
          Url.OrgAdmin.Forms.index({
            organization: org,
            formTemplateId: bulkTargets.formTemplateId,
            params: searchParams.toString(),
          })
        )
        .withDefault(null);

      if (formsListPath) {
        navigate(formsListPath, {
          replace: true,
          state: { clearBulkSelection: true },
        });
      } else {
        navigate(-1);
      }
    } catch (error: unknown) {
      console.error(error);
      toast.error({
        title: "Oops! Something went wrong!",
        description: "Check your network and try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Stack flexGrow="1" w="100%">
        {verificationSummaries.map((verificationSummary) => {
          const {
            verification,
            applicableAppIds,
            compositeStatus: existingCompositeStatus,
          } = verificationSummary;
          const newCompositeStatus = newStatuses.get(
            verification.id,
            existingCompositeStatus
          );

          const additionalInfo = isExclusionMode
            ? `${selectionSize} forms selected. Applicability is evaluated when processing.`
            : `${applicableAppIds.size} out of ${formIds.length} are applicable for this verification`;

          return (
            <VerificationInput
              key={verification.id}
              label={verification.label}
              showMixed={!isExclusionMode && existingCompositeStatus === Mixed}
              status={newCompositeStatus}
              onStatusChange={(status) =>
                setNewStatuses((statuses) =>
                  status === existingCompositeStatus
                    ? statuses.delete(verification.id)
                    : statuses.set(verification.id, status)
                )
              }
              additionalInfo={additionalInfo}
            />
          );
        })}
      </Stack>

      <AdminFormButtons>
        <Button
          variant="outline"
          colorScheme="gray"
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <Spacer />
        <Button
          variant="solid"
          type="submit"
          isDisabled={newStatuses.isEmpty() || isSubmitting}
          isLoading={isSubmitting}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </AdminFormButtons>
    </>
  );
};
